/**
 * Großhandel-Anfrage als X83 (F3) — Angebotsaufforderung OHNE Preise, z.B. an
 * den Großhandel senden. Forward-Adapter (shared/quote-to-gaeb) baut die
 * Titel-/Untertitel-Hierarchie aus der Angebotsstruktur; der bestehende
 * export-partial-Endpoint schreibt das XML. REINE Ausgabe: gespeicherte
 * Angebotsdaten bleiben unangetastet.
 *
 * Eigenständige Komponente im Stil von urkalkulation-export.tsx — herausgelöst
 * aus docx-export.tsx (Welle 1.3 UX-Entlastung), damit sie im "Export ▾"-Menü
 * der Angebots-Sidebar (quote-sidebar.tsx) gebündelt werden kann. Rendert
 * wahlweise als eigenständiger Button ODER (asMenuItem) als DropdownMenuItem.
 *
 * v1: ALLE Angebotspositionen (quoteItems). TODO(F3.2): Positionsauswahl wie
 * GaebTeilExport (Von/Bis-OZ, Gewerk-Filter) — shared/gaeb-partial.ts ist dafür
 * wiederverwendbar. TODO(F3.2): X81 (Leistungsbeschreibung, DA81) ist ein
 * eigenes GAEB-Schema — der Writer kann nur DA83/84/85; X83 ist für Anfragen
 * das fachlich korrekte Format.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Loader2, Send } from 'lucide-react';
import { useOfferStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';
import { getQuoteFileStem } from '@shared/quote-naming';
import { buildGaebRequestEntries, isGaebHeaderRow } from '@shared/quote-to-gaeb';
import { toSafeNumber } from '@shared/quote-totals';
import { isExcludedFromMainSum } from '@/lib/quote-calculations';

interface GrosshandelAnfrageExportProps {
  /** Als DropdownMenuItem rendern statt als eigenständiger Button (Export ▾-Menü). */
  asMenuItem?: boolean;
  /** Externer In-Flight-Guard aus dem persistenten Parent — siehe blanko-lv-export.tsx. */
  busy?: boolean;
  onRunningChange?: (running: boolean) => void;
}

export default function GrosshandelAnfrageExport({ asMenuItem = false, busy = false, onRunningChange }: GrosshandelAnfrageExportProps) {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const positions = useOfferStore((state) => state.positions);
  const gewerkOrder = useOfferStore((state) => state.gewerkOrder);

  const handleExport = async () => {
    // Doppelklick-/Doppelrequest-Schutz (lokal ODER laut Parent-Guard).
    if (busy || isExporting) return;
    try {
      onRunningChange?.(true);
      setIsExporting(true);
      const numberingRows = positions.map((position) => ({ ...position }));

      // B-07: Vor dem Bau prüfen, damit der Nutzer eine wahrheitsgemässe Meldung
      // bekommt statt der technischen Validierungsmeldung aus dem Adapter.
      const leistungsZeilen = quoteItems.filter((item) => !isGaebHeaderRow(item as any));
      if (leistungsZeilen.length === 0) {
        throw new Error('Keine Positionen im Angebot — bitte zuerst Positionen anlegen.');
      }
      // Dieselbe Regel wie im Adapter: Bedarfs-/Alternativpositionen sind nicht
      // summenwirksam und dürfen legitim ohne Menge bleiben.
      const ohneMenge = leistungsZeilen.filter((item) =>
        !isExcludedFromMainSum(item as any)
        && !(toSafeNumber((item as any).menge, 0) > 0),
      );
      if (ohneMenge.length > 0) {
        throw new Error(
          `${ohneMenge.length} von ${leistungsZeilen.length} Positionen haben keine Menge — `
          + 'eine Anfrage braucht für jede summenwirksame Position eine Menge größer 0.',
        );
      }

      const { entries, stats } = buildGaebRequestEntries(quoteItems, gewerkOrder, {
        numberingRows,
      });

      const stem = getQuoteFileStem({ header: quoteHeader }, 'Angebot');
      const filename = `${stem}_Anfrage.x83`.replace(/[^\w.\-]+/g, '_');

      const response = await fetch('/api/gaeb/export-partial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entries,
          metadata: {
            projectName: quoteHeader.bauvorhaben?.trim() || quoteHeader.angebot_nr || 'Anfrage',
            gaebVersion: '3.3',
          },
          exportFormat: 'x83',
          // Kern von F3: Anfrage-Semantik — Writer lässt UP/IT weg.
          omitPrices: true,
          filename,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Export fehlgeschlagen' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Großhandel-Anfrage exportiert',
        description: `${stats.itemCount} Position(en), ${stats.titleCount} Titel, ${stats.subtitleCount} Untertitel — ohne Preise (X83).`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Anfrage-Fehler', description: message, variant: 'destructive' });
    } finally {
      setIsExporting(false);
      onRunningChange?.(false);
    }
  };

  const disabled = busy || isExporting || quoteItems.length === 0;

  if (asMenuItem) {
    return (
      <DropdownMenuItem
        onSelect={() => {
          // KEIN preventDefault: einmalige Aktion, kein Dialog-Trigger — das
          // Menü darf nach der Auswahl normal schließen.
          handleExport();
        }}
        disabled={disabled}
        data-testid="button-export-gaeb-request"
        title="GAEB X83 (Angebotsaufforderung) ohne Preise erzeugen — z.B. an den Großhandel senden. Titel/Untertitel und OZ aus der Angebotsstruktur."
      >
        {isExporting ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Send className="mr-2 h-4 w-4" />
        )}
        Großhandel-Anfrage (X83)
      </DropdownMenuItem>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={handleExport}
      disabled={disabled}
      className="w-full"
      data-testid="button-export-gaeb-request"
      title="GAEB X83 (Angebotsaufforderung) ohne Preise erzeugen — z.B. an den Großhandel senden. Titel/Untertitel und OZ aus der Angebotsstruktur."
    >
      {isExporting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          X83 wird erstellt...
        </>
      ) : (
        <>
          <Send className="mr-2 h-4 w-4" />
          Großhandel-Anfrage (X83)
        </>
      )}
    </Button>
  );
}
